/*
 * Copyright 2022. NXGN Management, LLC. All Rights Reserved.
 */

import { Component, Input, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { AuthService, MerchantService, NotificationService } from "../../../services";
import { BeneficialOwner, Merchant } from "../../../models";
import { MerchantDetailsParentComponent } from "../merchant-details-parent.component";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { PATTERN_CONSTANTS } from "../../../utils/data.rest.constants";

@Component({
  selector: "app-beneficial-owner",
  templateUrl: "./beneficial-owner.component.html"
})
export class BeneficialOwnerComponent extends MerchantDetailsParentComponent implements OnInit {
  ownerForm!: FormGroup;
  @Input("loaded") loaded = false;
  @Input("merchant") merchant!: Merchant;
  editable = false;
  creatingNewOwner = false;
  editIndex = 0;
  actionInProgress = false;
  deleteBeneficialOwnerInProgress = false;

  constructor(
    router: Router,
    activatedRoute: ActivatedRoute,
    merchantService: MerchantService,
    notificationService: NotificationService,
    authService: AuthService,
    private fb: FormBuilder
  ) {
    super(router, activatedRoute, merchantService, notificationService, authService);
  }

  ngOnInit(): void {
    this.createForm();
  }

  sortOwners(beneficialOwners: BeneficialOwner[]): BeneficialOwner[] {
    return beneficialOwners.sort((a, b) => {
      if (a.ownerType === "ControlOwner" && b.ownerType !== "ControlOwner") {
        return -1;
      } else if (b.ownerType === "ControlOwner" && a.ownerType !== "ControlOwner") {
        return 1;
      } else {
        return a.ownerType.localeCompare(b.ownerType.toLowerCase(), undefined, { numeric: true });
      }
    });
  }

  createForm(): void {
    this.ownerForm = this.fb.group({
      firstName: new FormControl("", [Validators.required]),
      lastName: new FormControl("", [Validators.required]),
      title: new FormControl(""),
      addressLine1: new FormControl("", [Validators.required, Validators.maxLength(99)]),
      addressLine2: new FormControl("", [Validators.maxLength(99)]),
      city: new FormControl("", [Validators.required, Validators.maxLength(49)]),
      state: new FormControl("", [Validators.required]),
      zipCode: new FormControl("", [
        Validators.required,
        Validators.pattern(PATTERN_CONSTANTS.NUMBER),
        Validators.maxLength(11)
      ]),
      country: new FormControl("US", [Validators.required]),
      phoneNumber: new FormControl("", [Validators.pattern(PATTERN_CONSTANTS.NUMBER_10), Validators.maxLength(10)]),
      ssn: new FormControl("", [Validators.pattern("^([0-9]{9})$"), Validators.maxLength(9)]),
      percentOwned: new FormControl("", [
        Validators.required,
        Validators.pattern(PATTERN_CONSTANTS.PERCENT_3_DECIMALS),
        Validators.max(100),
        Validators.min(0)
      ]),
      dateOfBirth: new FormControl(""),
      email: new FormControl("", [Validators.email]),
      ownerType: new FormControl(""),
      contactType: new FormControl("")
    });
  }

  createOwner(): void {
    this.createForm();
    this.editable = false;
    this.creatingNewOwner = true;
  }

  findBeneficialOwnerContactType(): void {
    const missingOwner = this.getNextOwnerType(this.merchant.accountDetails.worldPayAccountDetails.beneficialOwners);
    if (missingOwner) {
      this.ownerForm.get("contactType")?.setValue({ label: missingOwner, value: missingOwner.replace(" ", "") });
      this.ownerForm.get("ownerType")?.setValue(missingOwner.replace(" ", ""));
    }
  }

  getNextOwnerType(owner: BeneficialOwner[]) {
    const currentOwners: number[] = [];
    owner.forEach((item: BeneficialOwner) => {
      currentOwners.push(Number(item.ownerType.match(/\d/g)));
    });
    let ownerType;
    for (let i = 1; i < 11; i++) {
      if (currentOwners.indexOf(i) === -1) {
        ownerType = "BeneficialOwner " + i;
        break;
      }
    }
    return ownerType;
  }

  createBeneficialOwner(): void {
    this.actionInProgress = true;
    this.findBeneficialOwnerContactType();
    const tempOwner: BeneficialOwner = this.ownerForm.value;
    tempOwner.dateOfBirth = this.merchantService.dateToMMddyyyy(this.ownerForm.get("dateOfBirth")?.value);
    this.merchantService.createBeneficialOwner(this.merchant.id, tempOwner).subscribe(
      (result) => {
        this.actionInProgress = false;
        this.merchant.accountDetails.worldPayAccountDetails.beneficialOwners.push(result);
        this.editable = false;
        this.creatingNewOwner = false;
      },
      (error) => {
        this.actionInProgress = false;
        this.notificationService.error(error);
      }
    );
  }

  editOwner(index: number): void {
    this.editable = true;
    this.editIndex = index;
    this.creatingNewOwner = false;
    this.ownerForm?.patchValue(this.merchant.accountDetails.worldPayAccountDetails.beneficialOwners[index]);
    if (this.merchant.accountDetails.worldPayAccountDetails.beneficialOwners[index].dateOfBirth) {
      this.ownerForm
        .get("dateOfBirth")
        ?.setValue(new Date(this.merchant.accountDetails.worldPayAccountDetails.beneficialOwners[index].dateOfBirth));
    }
  }

  isAddOrEdit(): boolean {
    return this.editable || this.creatingNewOwner;
  }

  updateBeneficialOwner(): void {
    if (this.ownerForm.valid) {
      this.actionInProgress = true;
      if (this.editable) {
        const tempOwner: BeneficialOwner = this.ownerForm.value;
        tempOwner.dateOfBirth = this.merchantService.dateToMMddyyyy(this.ownerForm.get("dateOfBirth")?.value);
        this.merchantService
          .updateBeneficialOwner(
            this.merchant.id,
            this.merchant.accountDetails.worldPayAccountDetails.beneficialOwners[this.editIndex].beneficialOwnerId,
            tempOwner
          )
          .subscribe(
            (result) => {
              this.actionInProgress = false;
              this.merchant.accountDetails.worldPayAccountDetails.beneficialOwners[this.editIndex] = result;
              this.editable = false;
              this.creatingNewOwner = false;
            },
            (error) => {
              this.actionInProgress = false;
              this.notificationService.error(error);
            }
          );
      } else {
        this.createBeneficialOwner();
      }
    } else {
      this.ownerForm.markAllAsTouched();
      this.actionInProgress = false;
    }
  }

  deleteBeneficialOwner(index: number, owner: BeneficialOwner): void {
    if (!this.deleteBeneficialOwnerInProgress) {
      this.deleteBeneficialOwnerInProgress = true;
      this.merchantService.deleteBeneficialOwner(this.merchant.id, owner.beneficialOwnerId).subscribe(
        () => {
          this.merchant.accountDetails.worldPayAccountDetails.beneficialOwners.splice(index, 1);
          this.deleteBeneficialOwnerInProgress = false;
        },
        (error) => {
          this.notificationService.error(error);
        }
      );
    }
  }

  cancelChanges(): void {
    this.editable = false;
    this.creatingNewOwner = false;
  }
}
